import type { CheckedState } from "@radix-ui/react-checkbox";
import type { FC, ReactNode } from "react";

import type { Option } from "@/components/molecules/SelectOptions/BaseSelectOptions";

import { CustomBadgeButton } from "../Badge";

interface TagsBaseProps {
  tagsList: Option[];
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  title?: string | ReactNode;
  optional?: boolean;
  multiple?: boolean;
}

const TagsBase: FC<TagsBaseProps> = ({
  tagsList,
  selected,
  onChange,
  disabled,
  title,
  optional = false,
  multiple = true,
}) => {
  const selectedArray = Array.isArray(selected) ? selected : [];

  const handleCheckedChange = (tag: Option, checked: CheckedState) => {
    let next: string[];

    if (multiple) {
      if (tag.exclusive && checked) {
        next = [tag.id];
      } else if (tag.exclusive && !checked) {
        next = [];
      } else {
        next = checked
          ? [
              ...selectedArray.filter(
                id => !tagsList.find(t => t.id === id && t.exclusive)
              ),
              tag.id,
            ]
          : selectedArray.filter(id => id !== tag.id);
      }
    } else {
      next = checked ? [tag.id] : [];
    }
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      {title && (
        <h3 className="text-sm font-[Inter] leading-[24px]">
          {title}{" "}
          {optional && <span className="text-gray-400">(optional)</span>}
        </h3>
      )}
      <div className="flex flex-wrap gap-[8px]">
        {tagsList.map(tag => {
          const isSelected = selectedArray.includes(tag.id);

          return (
            <CustomBadgeButton
              key={tag.id}
              checked={isSelected}
              onCheckedChange={checked => handleCheckedChange(tag, checked)}
              disabled={disabled}
              data-testid={`tags-base-tag-${tag.id}`}
            >
              {tag.name}
            </CustomBadgeButton>
          );
        })}
      </div>
    </div>
  );
};

export default TagsBase;
